import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Paperclip,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TableHead, TableHeader } from '@/components/ui/table'
import { SearchableSelect, type SearchableOption } from '@/components/ui/searchable-select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ActionItemDialog } from '@/components/action-items/ActionItemDialog'
import type { ActionItem, ActionItemPriority, ActionItemStatus } from '@/api/actionItems'
import {
  useActionItemAssignees,
  useActionItems,
  useAttachToActionItem,
  useRemoveActionItems,
} from '@/hooks/useActionItems'
import {
  ACTION_ITEMS_FORM_PARAM,
  ACTION_ITEMS_FROM_PARAM,
  ACTION_ITEMS_SUBMISSION_PARAM,
  ALL_STATUSES,
  COLUMNS,
  DEFAULT_FILTERS,
  PRIORITY_LABELS,
  STATUS_LABELS,
  assignedToLabel,
  isAllChecked,
  isPastDue,
  itemsCsv,
  localToday,
  matchesSearch,
  setAll,
  sortItems,
  sourceLink,
  toApiFilters,
  toggleStatus,
  type ActionItemFilterState,
  type ColumnKey,
} from '@/lib/actionItems'
import { downloadTextFile } from '@/lib/csv'
import { ATTACHMENT_ACCEPTED_MIME } from '@/types/cgt'
import { cn } from '@/lib/utils'

/** Rows per page, as the other paged tables offer them. */
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

const PRIORITY_STYLE: Record<ActionItemPriority, string> = {
  LOW: 'border-green-200 bg-green-50 text-green-700',
  MED: 'border-amber-200 bg-amber-50 text-amber-700',
  HIGH: 'border-red-200 bg-red-50 text-red-700',
}

const STATUS_STYLE: Record<ActionItemStatus, string> = {
  OPEN: 'border-slate-200 bg-slate-50 text-slate-600',
  ASSIGNED: 'border-blue-200 bg-blue-50 text-blue-700',
  STARTED: 'border-amber-200 bg-amber-50 text-amber-700',
  COMPLETED: 'border-green-200 bg-green-50 text-green-700',
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label className='flex cursor-pointer items-center gap-2 text-sm'>
      <input
        type='checkbox'
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className='h-4 w-4 rounded border-gray-300 accent-primary'
      />
      {label}
    </label>
  )
}

/**
 * Action Items (z8nrz7fkfh): every follow-up raised on a form or a record, in
 * one list. The filter bar applies on Go; search, sorting and paging act on
 * what it found.
 */
export function ActionItemsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  // Opened from a form's Action (n) badge: that form's items not yet completed.
  const submissionId = searchParams.get(ACTION_ITEMS_SUBMISSION_PARAM)
  // Add on that list raises the item on the same form.
  const formName = searchParams.get(ACTION_ITEMS_FORM_PARAM)
  const formPath = searchParams.get(ACTION_ITEMS_FROM_PARAM)

  const [draft, setDraft] = useState<ActionItemFilterState>(DEFAULT_FILTERS)
  const [applied, setApplied] = useState<ActionItemFilterState>(draft)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<{ key: ColumnKey; dir: 'asc' | 'desc' }>({ key: 'dueDate', dir: 'asc' })
  const [pageSize, setPageSize] = useState(10)
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [dialog, setDialog] = useState<{ item: ActionItem | null } | null>(null)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const filters = useMemo(() => toApiFilters(applied, submissionId ?? undefined), [applied, submissionId])
  const query = useActionItems(filters)
  const assignees = useActionItemAssignees()
  const remove = useRemoveActionItems()
  const attach = useAttachToActionItem()
  const today = localToday()

  const items = useMemo(() => (filters === null ? [] : (query.data ?? [])), [filters, query.data])
  const shown = useMemo(
    () => sortItems(items.filter(i => matchesSearch(i, search)), sort.key, sort.dir),
    [items, search, sort],
  )
  const pageCount = Math.max(1, Math.ceil(shown.length / pageSize))
  const pageRows = shown.slice(page * pageSize, page * pageSize + pageSize)

  // A narrower result can leave the page past the end.
  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1)
  }, [page, pageCount])

  // Selection only ever names rows still listed.
  useEffect(() => {
    setSelected(prev => {
      const live = new Set(items.map(i => i.id))
      const next = new Set([...prev].filter(id => live.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [items])

  const selectedItems = items.filter(i => selected.has(i.id))
  const canRemove = selectedItems.length > 0 && selectedItems.every(i => i.canRemove)
  const attachTarget = selectedItems.length === 1 && selectedItems[0].canEdit ? selectedItems[0] : null

  const userOptions = useMemo<SearchableOption[]>(
    () => [
      { value: '', label: 'All users' },
      ...(assignees.data?.users ?? []).map(u => ({ value: u.id, label: `${u.name} (${u.email})` })),
    ],
    [assignees.data],
  )
  const groupOptions = useMemo<SearchableOption[]>(
    () => [{ value: '', label: 'All groups' }, ...(assignees.data?.groups ?? []).map(g => ({ value: g.id, label: g.name }))],
    [assignees.data],
  )

  const go = () => {
    setApplied(draft)
    setPage(0)
  }
  const reset = () => {
    setDraft(DEFAULT_FILTERS)
    setApplied(DEFAULT_FILTERS)
    setSearch('')
    setPage(0)
    if (submissionId) setSearchParams({}, { replace: true })
  }

  const toggleSort = (key: ColumnKey) =>
    setSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))

  const allOnPage = pageRows.length > 0 && pageRows.every(r => selected.has(r.id))
  const togglePage = () =>
    setSelected(prev => {
      const next = new Set(prev)
      for (const r of pageRows) {
        if (allOnPage) next.delete(r.id)
        else next.add(r.id)
      }
      return next
    })
  const toggleRow = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const doRemove = async () => {
    const count = selectedItems.length
    try {
      await remove.mutateAsync(selectedItems.map(i => i.id))
      setSelected(new Set())
      setNotice({ tone: 'ok', text: `Removed ${count} action item${count === 1 ? '' : 's'}.` })
    } catch (e) {
      setNotice({ tone: 'error', text: e instanceof Error ? e.message : 'Could not remove.' })
    } finally {
      setConfirmRemove(false)
    }
  }

  const doAttach = async (files: File[]) => {
    if (!attachTarget || files.length === 0) return
    try {
      await attach.mutateAsync({ id: attachTarget.id, files })
      setNotice({
        tone: 'ok',
        text: `Attached ${files.length} file${files.length === 1 ? '' : 's'} to A#${attachTarget.number}.`,
      })
    } catch (e) {
      setNotice({ tone: 'error', text: e instanceof Error ? e.message : 'Could not attach the file.' })
    }
  }

  const exportAll = () => downloadTextFile(`action-items_${today}.csv`, itemsCsv(shown))

  return (
    <div className='space-y-4 p-4 md:p-6'>
      {/* Filters: applied on Go */}
      <div className='space-y-3 rounded-xl border bg-card p-4'>
        <div className='flex flex-wrap items-end gap-4'>
          <div className='w-56 space-y-1.5'>
            <Label>Group</Label>
            <SearchableSelect
              value={draft.groupId}
              onChange={v => setDraft(d => ({ ...d, groupId: v }))}
              options={groupOptions}
              placeholder='Select Group'
            />
          </div>
          <div className='w-56 space-y-1.5'>
            <Label>Created By</Label>
            <SearchableSelect
              value={draft.createdById}
              onChange={v => setDraft(d => ({ ...d, createdById: v }))}
              options={userOptions}
              placeholder='Select User'
            />
          </div>
          <div className='w-56 space-y-1.5'>
            <Label>Assigned To</Label>
            <SearchableSelect
              value={draft.assignedUserId}
              onChange={v => setDraft(d => ({ ...d, assignedUserId: v }))}
              options={userOptions}
              placeholder='Select User'
            />
          </div>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={reset}>
              Reset
            </Button>
            <Button onClick={go}>Go</Button>
          </div>
        </div>
        <div className='flex flex-wrap items-center gap-x-6 gap-y-2'>
          <Check
            label='Past Due & This Week'
            checked={draft.pastDueThisWeek}
            onChange={c => setDraft(d => ({ ...d, pastDueThisWeek: c }))}
          />
          {ALL_STATUSES.map(s => (
            <Check
              key={s}
              label={STATUS_LABELS[s]}
              checked={draft.statuses.includes(s)}
              onChange={() => setDraft(d => toggleStatus(d, s))}
            />
          ))}
          <Check label='All' checked={isAllChecked(draft)} onChange={c => setDraft(d => setAll(d, c))} />
        </div>
      </div>

      <div className='space-y-3 rounded-xl border bg-card p-4'>
        <div className='flex flex-wrap items-center gap-2'>
          <h1 className='mr-2 text-lg font-semibold'>Action Items</h1>
          {submissionId && (
            <span className='flex items-center gap-1 rounded-full border bg-muted px-2.5 py-0.5 text-xs'>
              On {formName ?? 'one form'}
              <button
                type='button'
                aria-label='Show every form'
                onClick={() => setSearchParams({}, { replace: true })}
                className='rounded-full hover:text-foreground'
              >
                <X className='h-3 w-3' />
              </button>
            </span>
          )}
        </div>

        <div className='flex flex-wrap items-center gap-2'>
          <Button onClick={() => setDialog({ item: null })}>
            <Plus className='mr-1.5 h-4 w-4' />
            Add
          </Button>
          <Button
            variant='outline'
            disabled={!canRemove}
            title={
              selectedItems.length === 0
                ? 'Select the items to remove'
                : canRemove
                  ? undefined
                  : 'Only whoever raised an item, or someone who manages action items, can remove it'
            }
            onClick={() => setConfirmRemove(true)}
          >
            <Trash2 className='mr-1.5 h-4 w-4' />
            Remove
          </Button>
          <label className='flex items-center gap-2 text-sm'>
            Number of Rows
            <select
              aria-label='Number of Rows'
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value))
                setPage(0)
              }}
              className='h-9 rounded-md border bg-background px-2 text-sm'
            >
              {PAGE_SIZE_OPTIONS.map(n => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <div className='relative w-64'>
            <Search className='pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
            <Input
              value={search}
              onChange={e => {
                setSearch(e.target.value)
                setPage(0)
              }}
              placeholder='Search...'
              aria-label='Search action items'
              className='pl-8'
            />
          </div>
          <Button variant='outline' onClick={exportAll} disabled={shown.length === 0}>
            <Download className='mr-1.5 h-4 w-4' />
            Export All Data
          </Button>
          <Button
            variant='outline'
            disabled={!attachTarget || attach.isPending}
            title={attachTarget ? `Attach files to A#${attachTarget.number}` : 'Select one item you can edit'}
            onClick={() => fileInput.current?.click()}
          >
            {attach.isPending ? (
              <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
            ) : (
              <Paperclip className='mr-1.5 h-4 w-4' />
            )}
            Attach
          </Button>
          <input
            ref={fileInput}
            type='file'
            multiple
            hidden
            accept={ATTACHMENT_ACCEPTED_MIME.join(',')}
            onChange={e => {
              const files = Array.from(e.target.files ?? [])
              e.target.value = ''
              void doAttach(files)
            }}
          />
        </div>

        {notice && (
          <p
            role={notice.tone === 'error' ? 'alert' : 'status'}
            className={cn('text-sm', notice.tone === 'error' ? 'text-destructive' : 'text-green-700')}
          >
            {notice.text}
          </p>
        )}

        <div className='overflow-x-persistent rounded-lg border'>
          <table className='w-full text-sm'>
            <TableHeader>
              <tr>
                <TableHead className='w-10'>
                  <Checkbox aria-label='Select all on this page' checked={allOnPage} onCheckedChange={togglePage} />
                </TableHead>
                {COLUMNS.map(c => (
                  <TableHead key={c.key} className='whitespace-nowrap'>
                    <button type='button' onClick={() => toggleSort(c.key)} className='inline-flex items-center gap-1 uppercase'>
                      {c.label}
                      <ArrowUpDown className={cn('h-3 w-3', sort.key === c.key ? 'opacity-100' : 'opacity-50')} />
                    </button>
                  </TableHead>
                ))}
              </tr>
            </TableHeader>
            <tbody>
              {query.isLoading && filters !== null ? (
                <tr>
                  <td colSpan={COLUMNS.length + 1} className='px-3 py-8 text-center text-muted-foreground'>
                    <Loader2 className='mx-auto h-5 w-5 animate-spin' />
                  </td>
                </tr>
              ) : query.isError ? (
                <tr>
                  <td colSpan={COLUMNS.length + 1} className='px-3 py-8 text-center text-destructive'>
                    {query.error instanceof Error ? query.error.message : 'Could not load action items.'}
                  </td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length + 1} className='px-3 py-8 text-center text-muted-foreground'>
                    No action items match these filters.
                  </td>
                </tr>
              ) : (
                pageRows.map(item => {
                  const link = sourceLink(item)
                  return (
                    <tr key={item.id} className='border-t hover:bg-muted/30'>
                      <td className='px-3 py-2'>
                        <input
                          type='checkbox'
                          aria-label={`Select A#${item.number}`}
                          checked={selected.has(item.id)}
                          onChange={() => toggleRow(item.id)}
                          className='h-4 w-4 accent-primary'
                        />
                      </td>
                      <td className='whitespace-nowrap px-3 py-2'>
                        <span className='inline-flex items-center gap-1.5'>
                          {item.number}
                          <button
                            type='button'
                            aria-label={`${item.canEdit ? 'Edit' : 'View'} A#${item.number}`}
                            title={item.canEdit ? 'Edit' : 'View'}
                            onClick={() => setDialog({ item })}
                            className='text-primary hover:text-primary/80'
                          >
                            <Pencil className='h-3.5 w-3.5' />
                          </button>
                        </span>
                      </td>
                      <td className='px-3 py-2'>
                        {link ? (
                          <Link
                            to={link}
                            title={item.sourceLabel ?? undefined}
                            className='text-violet-700 underline hover:text-violet-900'
                          >
                            {item.sourceArea}
                          </Link>
                        ) : (
                          <span title={item.sourceLabel ?? undefined}>{item.sourceArea}</span>
                        )}
                      </td>
                      <td className='px-3 py-2'>{item.createdByName}</td>
                      <td className='max-w-xs px-3 py-2'>{item.description}</td>
                      <td className='px-3 py-2'>{assignedToLabel(item)}</td>
                      <td className='px-3 py-2'>
                        <span
                          className={cn(
                            'inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium',
                            PRIORITY_STYLE[item.priority],
                          )}
                        >
                          {PRIORITY_LABELS[item.priority]}
                        </span>
                      </td>
                      <td className='whitespace-nowrap px-3 py-2'>{localToday(new Date(item.createdAt))}</td>
                      <td
                        className={cn(
                          'whitespace-nowrap px-3 py-2',
                          isPastDue(item, today) && 'font-medium text-red-600',
                        )}
                      >
                        {item.dueDate}
                      </td>
                      <td className='max-w-xs px-3 py-2'>
                        {item.comments}
                        {item.attachments.length > 0 && (
                          <span
                            className='ml-1 inline-flex items-center gap-0.5 text-xs text-muted-foreground'
                            title={item.attachments.map(a => a.fileName).join(', ')}
                          >
                            <Paperclip className='h-3 w-3' />
                            {item.attachments.length}
                          </span>
                        )}
                      </td>
                      <td className='px-3 py-2'>
                        <span
                          className={cn(
                            'inline-block rounded-full border px-2.5 py-0.5 text-xs font-semibold uppercase',
                            STATUS_STYLE[item.status],
                          )}
                        >
                          {STATUS_LABELS[item.status]}
                        </span>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {shown.length > 0 && (
          <div className='flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground'>
            <span>
              {page * pageSize + 1}–{Math.min(shown.length, (page + 1) * pageSize)} of {shown.length}
            </span>
            <div className='flex items-center gap-1'>
              <button
                type='button'
                className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
              >
                <ChevronLeft className='h-3.5 w-3.5' />
                Previous
              </button>
              <button
                type='button'
                className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                disabled={page >= pageCount - 1}
                onClick={() => setPage(p => p + 1)}
              >
                Next
                <ChevronRight className='h-3.5 w-3.5' />
              </button>
            </div>
          </div>
        )}
      </div>

      <ActionItemDialog
        open={dialog !== null}
        onOpenChange={o => !o && setDialog(null)}
        item={dialog?.item ?? null}
        formSource={submissionId ? { submissionId, formName, path: formPath } : undefined}
        onSaved={saved =>
          setNotice({ tone: 'ok', text: dialog?.item ? `Saved A#${saved.number}.` : `Added A#${saved.number}.` })
        }
      />

      <Dialog open={confirmRemove} onOpenChange={o => !remove.isPending && setConfirmRemove(o)}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>
              Remove {selectedItems.length} action item{selectedItems.length === 1 ? '' : 's'}?
            </DialogTitle>
            <DialogDescription>
              {selectedItems.map(i => `A#${i.number}`).join(', ')} will no longer be listed. Each stays on the audit
              trail with who removed it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setConfirmRemove(false)} disabled={remove.isPending}>
              Cancel
            </Button>
            <Button variant='destructive' onClick={() => void doRemove()} disabled={remove.isPending}>
              {remove.isPending && <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
